import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { GraduationCap, Loader2 } from 'lucide-react';
import EmailOtpForm from '@/components/EmailOtpForm';
import PinLoginForm from '@/components/PinLoginForm';
import { setLastSchoolSlug } from '@/lib/schoolSession';

const API = '/api';

/**
 * School-branded login — Epic E (branding extended in Ticket 1A.4).
 *
 * Routed at `/school/:slug/login`. Uses public /api/schools/by-slug/:slug
 * to load name + logo + login_bg + brand_color, and renders the login
 * card with that branding.
 */
export default function SchoolLogin() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('otp');

  useEffect(() => {
    let cancelled = false;
    // Persist the slug as soon as the page mounts — this gives the 401
    // interceptor a recovery target even before the school doc resolves.
    if (slug) setLastSchoolSlug(slug);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/schools/by-slug/${slug}`);
        if (!cancelled) setSchool(data);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.status === 404
            ? 'School not found.'
            : 'Unable to load school. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-indigo-50 to-white">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading school…</p>
        </div>
      </div>
    );
  }

  if (error || !school) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-indigo-50 to-white px-4">
        <div className="text-center max-w-sm" data-testid="school-login-error">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <GraduationCap className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-3">
            {error || 'School unavailable'}
          </h2>
          <p className="text-gray-500 text-sm mb-8 leading-relaxed">
            Check the link your school shared, or contact your administrator.
          </p>
          <button
            onClick={() => navigate('/')}
            data-testid="school-login-back-home"
            className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
          >
            ← Back to Yukti home
          </button>
        </div>
      </div>
    );
  }

  // Slice 1 — deactivated school: render branded shell with inactive banner instead of login.
  if (school.is_active === false) {
    const brandColor = school.brand_color || '#4F46E5';
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white px-4"
        data-testid="school-login-deactivated"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
        >
          {school.logo_url ? (
            <img
              src={school.logo_url}
              alt={school.name}
              className="w-16 h-16 mx-auto mb-3 rounded-2xl object-contain bg-white shadow-md p-1"
            />
          ) : (
            <div
              className="w-16 h-16 mx-auto mb-3 rounded-2xl flex items-center justify-center shadow-md"
              style={{ backgroundColor: brandColor }}
            >
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{school.name}</h1>
          <div className="bg-amber-50 border border-amber-200 rounded-2xl px-6 py-8 mt-6">
            <p className="text-sm font-semibold text-amber-900 mb-2">
              School Currently Inactive
            </p>
            <p className="text-sm text-amber-800 leading-relaxed">
              {school.name}'s Yukti access is paused. Please contact your school
              administrator to restore access.
            </p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="mt-6 text-sm font-medium hover:underline"
            style={{ color: brandColor }}
            data-testid="school-login-deactivated-home-link"
          >
            ← Back to Yukti home
          </button>
        </motion.div>
      </div>
    );
  }

  const brandColor = school.brand_color || '#4F46E5';
  const bgUrl = school.login_bg_url || null;
  const hasBg = Boolean(bgUrl);

  const pageStyle = hasBg
    ? {
        backgroundImage: `linear-gradient(rgba(15,23,42,0.55), rgba(15,23,42,0.72)), url(${bgUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : { background: 'linear-gradient(to bottom, #eef2ff, #ffffff)' };

  return (
    <div
      className="adult-layout min-h-screen flex items-center justify-center px-4 py-10"
      style={pageStyle}
      data-testid="school-login-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-6">
          {school.logo_url ? (
            <img
              src={school.logo_url}
              alt={school.name}
              className="w-16 h-16 mx-auto mb-3 rounded-2xl object-contain bg-white shadow-md p-1"
              data-testid="school-login-logo"
            />
          ) : (
            <div
              className="w-16 h-16 mx-auto mb-3 rounded-2xl flex items-center justify-center shadow-md"
              style={{ backgroundColor: brandColor }}
            >
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
          )}
          <h1
            className={`text-2xl font-bold ${hasBg ? 'text-white' : 'text-gray-900'}`}
            data-testid="school-login-name"
          >
            {school.name}
          </h1>
          {school.board && (
            <p className={`text-sm mt-1 ${hasBg ? 'text-white/85' : 'text-gray-500'}`}>
              {school.board}
            </p>
          )}
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8 border border-indigo-100">
          <p className="text-sm text-gray-600 mb-6">
            Staff &amp; students sign in with the email address registered with your school.
          </p>

          <div className="flex gap-2 mb-5 hidden">
            {/* Email + PIN tab hidden per product decision; restore by removing parent's "hidden" class. */}
            <TabButton active={mode === 'otp'} onClick={() => setMode('otp')} testId="school-auth-tab-otp">
              Email code
            </TabButton>
            <TabButton active={mode === 'pin'} onClick={() => setMode('pin')} testId="school-auth-tab-pin">
              Email + PIN
            </TabButton>
          </div>

          {mode === 'otp' ? (
            <EmailOtpForm schoolSlug={slug} />
          ) : (
            <PinLoginForm />
          )}

          <style>{`[data-testid="email-otp-send-button"],[data-testid="email-otp-verify-button"],[data-testid="pin-login-submit-button"]{background:${brandColor} !important;}[data-testid="email-otp-send-button"]:hover,[data-testid="email-otp-verify-button"]:hover,[data-testid="pin-login-submit-button"]:hover{filter:brightness(0.92);}[data-testid="email-otp-resend-button"]{color:${brandColor} !important;}`}</style>
        </div>

        <p className={`text-center text-xs mt-6 ${hasBg ? 'text-white/70' : 'text-gray-400'}`}>
          Not a {school.name} member?{' '}
          <button
            type="button"
            onClick={() => navigate('/')}
            className="underline"
            style={{ color: hasBg ? 'white' : brandColor }}
            data-testid="school-login-home-link"
          >
            Yukti home
          </button>
        </p>
      </motion.div>
    </div>
  );
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex-1 py-2 text-sm rounded-lg font-medium transition ${
        active
          ? 'bg-indigo-100 text-indigo-700'
          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  );
}
